import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import {
  CommonModule,
  DatePipe,
  NgClass,
  NgForOf,
  NgIf
} from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApecoServicesService, DataTableService } from '../../services';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';
import { LoaderComponent } from '../loader/loader.component';
import { IRequest, IRequestData } from '../../models/IRequest';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { ArabicDatePipe } from '../../pipes/arabic-date.pipe';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { SearchComponent } from '../search/search.component';

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzPaginationModule,
    NgIf,
    TranslateModule,
    NzSelectComponent,
    NzOptionComponent,
    NzInputGroupComponent,
    NzIconDirective,
    NzInputDirective,
    NzToolTipModule,
    NzButtonComponent,
    FormsModule,
    NgForOf,
    LoaderComponent,
    NzSpinComponent,
    NzDatePickerModule,
    NgClass,
    DatePipe,
    ArabicDatePipe,
    SearchComponent
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss'
})
export class DataTableComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private apecoService = inject(ApecoServicesService);
  private endSubscription = new Subject();
  @ViewChild('pickerWrapper', { static: false }) pickerWrapper!: ElementRef;
  withFilter = input(false, { alias: 'withFilter' });
  withPagination = input(true, { alias: 'withPagination' });
  apiUrl = input('', { alias: 'api' });
  pageSize = input(20, { alias: 'pageSize' });
  param = input<any>(null, { alias: 'param' });
  tableService = inject(DataTableService);
  pageIndex = 1;
  totalElements = 20;
  searchLoading = false;
  data: IRequestData[] = [];
  loading = signal(true);
  date = null;
  selectedStatus = '';
  selectedService = '';
  searchKeyword: string | null = '';
  dateFilter: string[] = [];
  lang = localStorage.getItem('lang') as string;

  status = signal([
    { key: 0, label: 'draft' },
    { key: 1, label: 'in-progress' },
    { key: 3, label: 'rejected' },
    { key: 4, label: 'closed' },
    { key: 5, label: 'return-for-correction' }
  ]);

  services = signal<any[]>([]);

  ngOnInit(): void {
    if (this.param() || this.param() === 0) {
      this.selectedStatus = this.param();
      this.changeStatus();
    } else {
      this.getTableData();
    }

    this.apecoService.getServicesAsLookup().subscribe({
      next: (res) => {
        this.services.set(res);
      },
      error: (err) => {
        console.log(err);
      }
    });

    this.translateService.onLangChange
      .pipe(takeUntil(this.endSubscription))
      .subscribe((res) => {
        this.lang = res.lang;
      });
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      const inputEls =
        this.pickerWrapper?.nativeElement?.querySelectorAll('input');

      if (inputEls?.length === 2) {
        const [startInput, endInput] = inputEls;
        startInput.addEventListener('focus', () => this.moveActiveBarToRight());
        endInput.addEventListener('focus', () => this.moveActiveBarToRight());
        clearInterval(interval);
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.endSubscription.next(null);
  }

  onSearch(keyword: string | null) {
    this.searchKeyword = keyword;
    const req = this.buildRequest(1);
    this.getFilter(req);
  }

  getTableData() {
    this.loading.set(true);

    this.tableService
      .get(this.apiUrl(), {
        PageIndex: this.pageIndex,
        PageSize: this.pageSize()
      })
      .subscribe(
        (res: IRequest) => {
          this.data = res.data ?? res.servicesRequests;
          this.totalElements = res.count;
          this.loading.set(false);
        },
        (error) => {
          console.log(error);
          this.loading.set(false);
        }
      );
  }

  loadData(event: number) {
    const req = this.buildRequest(event);
    this.getFilter(req);
    this.pageIndex = event;
  }

  getFilter(req: any) {
    this.loading.set(true);

    this.tableService
      .get(this.apiUrl(), req)
      .pipe(debounceTime(300))
      .subscribe(
        (res: IRequest) => {
          this.loading.set(false);
          this.data = res?.data ?? res?.servicesRequests;
          this.totalElements = res.count;
        },
        (error) => {
          console.log(error);
          this.loading.set(false);
        }
      );
  }

  navigate(id: number, _: string) {
    this.router.navigate(['/request-details/' + id]);
  }

  onChange(event: string[]) {
    this.dateFilter = event;
    const req = this.buildRequest(1);
    this.getFilter(req);
  }

  changeStatus() {
    const req = this.buildRequest(1);
    this.getFilter(req);
  }

  buildRequest(pageIndex: number) {
    let req;

    if (this.dateFilter.length > 0) {
      req = {
        PageIndex: pageIndex,
        PageSize: this.pageSize(),
        'CreationDate.DateFrom': new Date(this.dateFilter[0]).toISOString(),
        'CreationDate.DateTo': new Date(this.dateFilter[1]).toISOString(),
        Status: this.selectedStatus,
        ServiceId: this.selectedService,
        RequestNumber: this.searchKeyword
      };
    } else {
      req = {
        PageIndex: pageIndex,
        PageSize: this.pageSize(),
        Status: this.selectedStatus,
        ServiceId: this.selectedService,
        RequestNumber: this.searchKeyword
      };
    }

    return req;
  }

  navigateToServices() {
    this.router.navigate(['/services']);
  }

  moveActiveBarToRight(): void {
    if (this.lang !== 'ar') {
      return;
    }

    setTimeout(() => {
      const activeBar = document.querySelector(
        '.ant-picker-active-bar'
      ) as HTMLElement;

      if (activeBar && activeBar.style.left) {
        const leftValue = parseFloat(activeBar.style.left.replace('px', ''));
        activeBar.style.left = '0';
        activeBar.style.right = `${leftValue}px`;
      }
    }, 50);
  }
}
